import * as React from 'react'

import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'tw:group/button tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-md tw:border tw:border-transparent tw:bg-clip-padding tw:text-app-dense-medium tw:whitespace-nowrap tw:transition-all tw:outline-none tw:select-none tw:focus-visible:border-ring tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50 tw:active:not-aria-[haspopup]:translate-y-px tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:aria-invalid:border-destructive tw:aria-invalid:ring-3 tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:border-destructive/50 tw:dark:aria-invalid:ring-destructive/40 tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-4',
  {
    variants: {
      variant: {
        default:
          'tw:bg-primary tw:text-primary-foreground tw:[a]:hover:bg-primary/80',
        outline:
          'tw:border-border tw:bg-background tw:hover:bg-muted tw:hover:text-foreground tw:aria-expanded:bg-muted tw:aria-expanded:text-foreground tw:dark:border-input tw:dark:bg-input/30 tw:dark:hover:bg-input/50',
        'primary-outline':
          'tw:border-border tw:bg-background tw:text-primary tw:hover:bg-primary-soft tw:hover:text-primary-soft-foreground tw:aria-expanded:bg-primary-soft tw:aria-expanded:text-primary-soft-foreground tw:dark:border-input tw:dark:bg-input/30',
        'destructive-outline':
          'tw:border-destructive tw:bg-background tw:text-destructive tw:hover:bg-destructive/10 tw:aria-expanded:bg-destructive/10 tw:dark:bg-input/30',
        'success-outline':
          'tw:border-success tw:bg-background tw:text-success tw:hover:bg-success/10 tw:aria-expanded:bg-success/10 tw:dark:bg-input/30',
        'primary-ghost':
          'tw:text-primary tw:hover:bg-primary-soft tw:hover:text-primary-soft-foreground tw:aria-expanded:bg-primary-soft tw:aria-expanded:text-primary-soft-foreground',
        'destructive-ghost':
          'tw:text-destructive tw:hover:bg-destructive-soft tw:hover:text-destructive-soft-foreground tw:aria-expanded:bg-destructive-soft tw:aria-expanded:text-destructive-soft-foreground',
        secondary:
          'tw:bg-secondary tw:text-secondary-foreground tw:hover:bg-secondary/80 tw:aria-expanded:bg-secondary tw:aria-expanded:text-secondary-foreground',
        ghost:
          'tw:hover:bg-muted tw:hover:text-foreground tw:aria-expanded:bg-muted tw:aria-expanded:text-foreground tw:dark:hover:bg-muted/50',
        destructive:
          'tw:bg-destructive/10 tw:text-destructive tw:hover:bg-destructive/20 tw:focus-visible:border-destructive/40 tw:focus-visible:ring-destructive/20 tw:dark:bg-destructive/20 tw:dark:hover:bg-destructive/30 tw:dark:focus-visible:ring-destructive/40',
        success:
          'tw:bg-success/10 tw:text-success tw:hover:bg-success/20 tw:focus-visible:border-success/40 tw:focus-visible:ring-success/20 tw:dark:bg-success/20 tw:dark:hover:bg-success/30 tw:dark:focus-visible:ring-success/40',
        link: 'tw:text-primary tw:underline-offset-4 tw:hover:underline',
      },
      size: {
        default:
          'tw:h-8 tw:gap-1.5 tw:px-2.5 tw:has-data-[icon=inline-end]:pr-2 tw:has-data-[icon=inline-start]:pl-2',
        xs: 'tw:h-6 tw:gap-1 tw:rounded-[min(var(--radius-md),10px)] tw:px-2 tw:in-data-[slot=button-group]:rounded-md tw:has-data-[icon=inline-end]:pr-1.5 tw:has-data-[icon=inline-start]:pl-1.5 tw:[&_svg:not([class*=size-])]:size-3',
        sm: 'tw:h-7 tw:gap-1 tw:rounded-[min(var(--radius-md),12px)] tw:px-2.5 tw:in-data-[slot=button-group]:rounded-md tw:has-data-[icon=inline-end]:pr-1.5 tw:has-data-[icon=inline-start]:pl-1.5 tw:[&_svg:not([class*=size-])]:size-3.5',
        lg: 'tw:h-9 tw:gap-1.5 tw:px-2.5 tw:has-data-[icon=inline-end]:pr-2 tw:has-data-[icon=inline-start]:pl-2',
        icon: 'tw:size-8',
        'icon-xs':
          'tw:size-6 tw:rounded-[min(var(--radius-md),10px)] tw:in-data-[slot=button-group]:rounded-md tw:[&_svg:not([class*=size-])]:size-3',
        'icon-sm':
          'tw:size-7 tw:rounded-[min(var(--radius-md),12px)] tw:in-data-[slot=button-group]:rounded-md',
        'icon-lg': 'tw:size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

// `React.forwardRef` is required for refs to reach the inner `ButtonPrimitive`
// in React 17. Without it, base-ui composition patterns that use `render={<Button>}`
// (e.g. DropdownMenu/Popover trigger composition) silently fail because base-ui
// can't wire onClick/handlers to the button DOM via ref.
const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonPrimitive.Props & VariantProps<typeof buttonVariants>
>(function Button(
  { className, variant = 'default', size = 'default', ...props },
  ref,
) {
  return (
    <ButtonPrimitive
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
})

export { Button, buttonVariants }
